import { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { updateAgent } from '../data';
import {
  ESCALATION_CHANNELS,
  escalationTargetLabel,
  escalationTargetPlaceholder,
  escalationChannelHint,
} from '../escalation_meta';
import './AgentPages.css';
import { Zap, Users, ClipboardList, FileText } from 'lucide-react';

const triggerSuggestions = ["Customer requests human", "Low confidence", "Billing dispute", "Security concern"];
const contextSuggestions = ["Full conversation", "Customer info", "Issue summary", "Attempted solutions"];
const AUTOSAVE_DELAY_MS = 800;

const defaultEscalationInstructions = `When escalation contacts are provided, use the appropriate integration to notify the relevant contact. You do not need confirmation to escalate.`;

export default function AgentEscalation() {
  const { agent, setAgent, agentId } = useOutletContext();
  const [status, setStatus] = useState('idle'); // 'idle' | 'pending' | 'saving' | 'saved' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  const [form, setForm] = useState({
    escalationWhen: '',
    escalationChannel: 'none',
    escalationWho: '',
    escalationContext: '',
    escalationInstructions: defaultEscalationInstructions
  });

  const hydratedRef = useRef(false);
  const timerRef = useRef(null);
  const latestRef = useRef(form);
  const savedTimerRef = useRef(null);

  useEffect(() => {
    if (agent) {
      const next = {
        escalationWhen: agent.escalationWhen || '',
        escalationChannel: agent.escalationChannel || 'none',
        escalationWho: agent.escalationWho || '',
        escalationContext: agent.escalationContext || '',
        escalationInstructions: agent.escalationInstructions || defaultEscalationInstructions
      };
      setForm(next);
      latestRef.current = next;
      hydratedRef.current = true;
    }
  }, [agent]);

  const flushSave = useCallback(async () => {
    const payload = latestRef.current;
    setStatus('saving');
    setErrorMsg('');
    try {
      await updateAgent(agentId, payload);
      setAgent((prev) => ({ ...(prev || {}), ...payload }));
      setStatus('saved');
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => {
        setStatus((s) => (s === 'saved' ? 'idle' : s));
      }, 1500);
    } catch (err) {
      console.error('Auto-save failed:', err);
      setErrorMsg(err?.message || 'Save failed');
      setStatus('error');
    }
  }, [agentId, setAgent]);

  const scheduleSave = useCallback(() => {
    if (!hydratedRef.current) return;
    setStatus('pending');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      flushSave();
    }, AUTOSAVE_DELAY_MS);
  }, [flushSave]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);

  useEffect(() => {
    const onBeforeUnload = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        flushSave();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [flushSave]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      latestRef.current = next;
      return next;
    });
    scheduleSave();
  };

  const addTo = (field, text) => {
    const current = form[field];
    handleChange(field, current ? `${current}\n• ${text}` : `• ${text}`);
  };

  const statusLabel = (() => {
    switch (status) {
      case 'pending': return 'Editing…';
      case 'saving': return 'Saving…';
      case 'saved': return 'Saved';
      case 'error': return errorMsg || 'Save failed';
      default: return 'All changes saved';
    }
  })();

  return (
    <div className="config-page">
      <div className="config-header">
        <h1>Escalation & Handoff</h1>
        <p>Define when and how to escalate to humans</p>
        <span className={`autosave-badge autosave-${status}`}>{statusLabel}</span>
      </div>

      <div className="config-section">
        <div className="section-header">
          <div className="section-icon"><Zap size={18} /></div>
          <div className="section-text">
            <h2>When to Escalate</h2>
            <p>Triggers that hand off to a human</p>
          </div>
        </div>

        <div className="form-group">
          <textarea
            className="input textarea"
            placeholder="• Customer requests human&#10;• Agent confidence is low&#10;• Security concern"
            value={form.escalationWhen}
            onChange={(e) => handleChange('escalationWhen', e.target.value)}
          />
          <div className="suggestions">
            <div className="suggestions-title">Common triggers</div>

            <div className="suggestions-list">
              {triggerSuggestions.map((s, i) => (
                <button key={i} className="suggestion" onClick={() => addTo('escalationWhen', s)}>
                  + {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="config-section">
        <div className="section-header">
          <div className="section-icon"><Users size={18} /></div>
          <div className="section-text">
            <h2>Who to Escalate To</h2>
            <p>Pick a channel and where the message should land</p>
          </div>
        </div>

        <div className="form-group">
          <label className="field-label">Notify via</label>
          <select
            className="input"
            value={form.escalationChannel}
            onChange={(e) => handleChange('escalationChannel', e.target.value)}
          >
            {ESCALATION_CHANNELS.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <p className="field-hint">{escalationChannelHint(form.escalationChannel)}</p>
        </div>

        <div className="form-group">
          <label className="field-label">{escalationTargetLabel(form.escalationChannel)}</label>
          <input
            className="input"
            type="text"
            value={form.escalationWho}
            disabled={form.escalationChannel === 'none'}
            placeholder={escalationTargetPlaceholder(form.escalationChannel)}
            onChange={(e) => handleChange('escalationWho', e.target.value)}
          />
        </div>
      </div>

      <div className="config-section">
        <div className="section-header">
          <div className="section-icon"><ClipboardList size={18} /></div>
          <div className="section-text">
            <h2>Handoff Context</h2>
            <p>What info to pass to humans</p>
          </div>
        </div>

        <div className="form-group">
          <textarea
            className="input textarea"
            placeholder="• Full conversation&#10;• Customer info&#10;• Issue summary"
            value={form.escalationContext}
            onChange={(e) => handleChange('escalationContext', e.target.value)}
          />
          <div className="suggestions">
            <div className="suggestions-title">Recommended</div>
            <div className="suggestions-list">
              {contextSuggestions.map((s, i) => (
                <button key={i} className="suggestion" onClick={() => addTo('escalationContext', s)}>
                  + {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="config-section">
        <div className="section-header">
          <div className="section-icon"><FileText size={18} /></div>
          <div className="section-text">
            <h2>Escalation Instructions</h2>
            <p>How the agent should handle escalations</p>
          </div>
        </div>

        <div className="form-group">
          <textarea
            className="input textarea"
            placeholder="Instructions for how the agent should escalate issues..."
            value={form.escalationInstructions}
            onChange={(e) => handleChange('escalationInstructions', e.target.value)}
          />
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => handleChange('escalationInstructions', defaultEscalationInstructions)}
            style={{ marginTop: '8px' }}
          >
            Reset to Default
          </button>
        </div>
      </div>
    </div>
  );
}
